import { useState, useEffect } from "react";
import { Dialog } from "../../../../components/common/dialog";
import { Text } from "../../../../components/common/typography";
import { Input } from "../../../../components/common/input";
import { DatePicker } from "../../../../components/common/date-picker";
import { TimePicker } from "../../../../components/common/time-picker";

function todayYMD(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

interface MarkInDeliveryDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onConfirm: (datePlanned: string, deliveryUrl?: string) => Promise<void>;
}

export function MarkInDeliveryDialog({
  open,
  setOpen,
  onConfirm,
}: MarkInDeliveryDialogProps) {
  const [plannedDate, setPlannedDate] = useState<string>("");
  const [plannedTime, setPlannedTime] = useState<string>("09:00");
  const [deliveryUrl, setDeliveryUrl] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      // Set default to tomorrow at 9 AM.
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);

      const year = tomorrow.getFullYear();
      const month = String(tomorrow.getMonth() + 1).padStart(2, "0");
      const day = String(tomorrow.getDate()).padStart(2, "0");

      setPlannedDate(`${year}-${month}-${day}`);
      setPlannedTime("09:00");
      setDeliveryUrl("");
      setError(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!plannedDate) {
      setError("Please select an expected delivery date");
      return;
    }

    // Convert separate date + time to API format (YYYY-MM-DD HH:mm:ss)
    const dateObj = new Date(`${plannedDate}T${plannedTime || "00:00"}`);
    if (isNaN(dateObj.getTime())) {
      setError("Please enter a valid date and time");
      return;
    }

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    const hours = String(dateObj.getHours()).padStart(2, "0");
    const minutes = String(dateObj.getMinutes()).padStart(2, "0");
    const seconds = String(dateObj.getSeconds()).padStart(2, "0");

    const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

    setIsSubmitting(true);
    setError(null);

    try {
      await onConfirm(formattedDate, deliveryUrl.trim() || undefined);
      setOpen(false);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to mark order in delivery. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setOpen(false);
      setError(null);
      setDeliveryUrl("");
    }
  };

  return (
    <Dialog
      open={open}
      setOpen={handleClose}
      title="Mark In Delivery"
      showCloseButton={true}
      showButtons={true}
      onConfirm={handleSubmit}
      buttonText={isSubmitting ? "Submitting..." : "Mark In Delivery"}
      cancelText="Cancel"
      width="max-w-md"
      content={
        <div className="space-y-4">
          <Text variant="body" tone="body">
            Select the expected delivery date and time and optionally provide a
            tracking link.
          </Text>

          <div className="space-y-4">
            <div className="space-y-2">
              <Text variant="bodyLg" tone="dark" className="block">
                Expected Delivery Date & Time
                <span className="text-red ml-0.5">*</span>
              </Text>
              <div className="grid grid-cols-2 gap-2">
                <DatePicker
                  value={plannedDate}
                  onChange={(v) => {
                    setPlannedDate(v);
                    setError(null);
                  }}
                  min={todayYMD()}
                  size="lg"
                />
                <TimePicker
                  value={plannedTime}
                  onChange={(v) => {
                    setPlannedTime(v);
                    setError(null);
                  }}
                  size="lg"
                />
              </div>
            </div>

            <Input
              label="Tracking Link (Optional)"
              type="text"
              name="deliveryUrl"
              value={deliveryUrl}
              onChange={(e) => setDeliveryUrl(e.target.value)}
              placeholder="https://tracking-provider.com/track/ABC123"
              wrapperClassName="mb-0"
            />

            {error && (
              <Text variant="bodyXs" className="text-accentError">
                {error}
              </Text>
            )}
          </div>
        </div>
      }
    />
  );
}
